import { useEffect, lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { TeamProvider } from "@/contexts/TeamContext";
import { TagsProvider } from "@/contexts/TagsContext";
import { FamilyProvider } from "@/contexts/FamilyContext";
import { ThemeProvider } from "@/components/ThemeProvider";
import { SettingsProvider } from "@/contexts/SettingsContext";
import { AuthGate } from "@/components/AuthGate";
import { AuthQueryCacheBuster } from "@/components/AuthQueryCacheBuster";
import { toast } from "@/lib/toast";
import { useErrorLogging } from "@/hooks/useErrorLogging";
import { EnvironmentThemeHandler } from "@/components/EnvironmentThemeHandler";
import { TaskEventHandler } from "./components/TaskEventHandler";
import { StockEventHandler } from "./components/StockEventHandler";
import { MealEventHandler } from "./components/MealEventHandler";
import { FailedSyncAutoRetry } from "./components/FailedSyncAutoRetry";
import { TeamThemeHandler } from "./components/TeamThemeHandler";
import { AppLoader } from "./components/AppLoader";
import { DiscoveryProvider } from "@/contexts/DiscoveryContext";
import { BackendStatusProvider } from "@/contexts/BackendStatusContext";
import { AppUpdateManager } from "./components/AppUpdateManager";
import { AppUsageTracker } from "./components/AppUsageTracker";
import { BackgroundPrefetcher } from "./components/BackgroundPrefetcher";
import { ToastSettingsManager } from "./components/ToastSettingsManager";
import { ErrorContextManager } from "./components/ErrorContextManager";
import { NetworkStatusBanner } from "./components/NetworkStatusBanner";
import { SkipToContent, SKIP_TO_CONTENT_TARGET_ID } from "./components/SkipToContent";
import { supabase } from "@/integrations/supabase/client";


// Lazy load pages for better performance
const Index = lazy(() => import("./pages/IndexRouter"));
const TeamPage = lazy(() => import("./pages/TeamPage"));
const InstallPage = lazy(() => import("./pages/InstallPage"));
const AdminPageComp = lazy(() => import("./pages/AdminPage"));
const AuthorizeDevicePage = lazy(() => import("./pages/AuthorizeDevicePage"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Integrations = lazy(() => import("./pages/Integrations"));
const WebhookHandler = lazy(() => import("./pages/WebhookHandler"));
const AppRedirect = lazy(() => import("./pages/AppRedirect"));
const Compare = lazy(() => import("./pages/Compare"));
const ShareTarget = lazy(() => import("./pages/ShareTarget"));
const CompareIndex = lazy(() => import("./pages/CompareIndex"));
const SuperAdminUsersView = lazy(() => import("./components/SuperAdminUsersView").then(m => ({ default: m.SuperAdminUsersView })));

// Lazy load handlers
const TaskActionHandler = lazy(() => import("./components/TaskActionHandler"));
const TaskCreateHandler = lazy(() => import("./components/TaskCreateHandler"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Keep previous data visible while refetching — prevents flashing
      refetchOnWindowFocus: false,
      // Do not refetch on mount when data is still within staleTime.
      // Combined with longer staleTime + localStorage caches, this prevents
      // request bursts when navigating between routes/tabs.
      refetchOnMount: false,
      // Don't refetch on reconnect by default — visibility handlers in our
      // hooks already cover the "tab returned" case with throttling.
      refetchOnReconnect: false,
      // Default stale time: 10 minutes. Hot data (tasks, stock) overrides
      // this with shorter values. Cold data (tags, teams, states, locations)
      // can stay stale much longer.
      staleTime: 10 * 60 * 1000,
      // Keep cached data in memory for 30 minutes after last use.
      gcTime: 30 * 60 * 1000,
    },
  },
});

const App = () => {
  // Initialize error logging (captures global errors and syncs to backend)
  useErrorLogging();

  // Listen for app update event
  useEffect(() => {
    const handleAppUpdated = () => {
      // Don't show the "App bijgewerkt" toast on native platforms
      // as it's confusing for users who expect updates via the App Store
      if ((window as any).Capacitor?.isNativePlatform()) return;

      toast.success("App bijgewerkt! 🎉", {
        channel: 'general',
        description: "De nieuwste versie is geïnstalleerd.",
        duration: 4000,
      });
    };

    window.addEventListener('app-updated', handleAppUpdated);
    return () => window.removeEventListener('app-updated', handleAppUpdated);
  }, []);

  // Handle Deep Links (for OAuth redirects on mobile)
  useEffect(() => {
    const handleAppUrlOpen = async (event: { url: string }) => {
      if (!event.url) return;

      console.log('[DeepLink] Processing URL:', event.url);

      try {
        const url = new URL(event.url);
        console.log('[DeepLink] Host:', url.host, 'Path:', url.pathname);

        // Handle Supabase auth callback
        // Check host (for app.clando:// scheme) and pathname (for https:// scheme)
        const isLoginCallback =
          url.host === 'login-callback' ||
          url.pathname.includes('login-callback') ||
          url.hash.includes('access_token');

        if (isLoginCallback) {
          console.log('[DeepLink] Detected login callback, extracting tokens...');
          // Tokens can be in hash (typical for OAuth) or search params
          const hash = url.hash.substring(1);
          const hashParams = new URLSearchParams(hash);

          let accessToken = hashParams.get('access_token');
          let refreshToken = hashParams.get('refresh_token');

          // Fallback to query parameters
          if (!accessToken) accessToken = url.searchParams.get('access_token');
          if (!refreshToken) refreshToken = url.searchParams.get('refresh_token');

          if (accessToken && refreshToken) {
            console.log('[DeepLink] Tokens found, setting session in Supabase...');
            toast.info("Inloggen verwerken...", { id: 'deep-link-loading' });

            const { error } = await supabase.auth.setSession({
              access_token: accessToken,
              refresh_token: refreshToken,
            });

            if (error) {
              console.error('[DeepLink] setSession error:', error.message);
              toast.error('Inloggen mislukt via deep link: ' + error.message);
            } else {
              console.log('[DeepLink] Session successfully established');
              toast.success('Succesvol ingelogd!', { id: 'deep-link-loading' });
            }
          } else {
            console.warn('[DeepLink] Callback detected but tokens were missing in URL components');
            console.log('[DeepLink] Hash length:', url.hash.length, 'Search length:', url.search.length);
          }
        }
      } catch (err) {
        console.error('[DeepLink] Error parsing URL:', err);
      }
    };

    // Listen for the appUrlOpen event from Capacitor App plugin
    const setupDeepLinkListener = async () => {
      try {
        const { App: CapApp } = await import('@capacitor/app');

        // Listen for new URL events while app is running
        CapApp.addListener('appUrlOpen', (event) => {
          console.log('[DeepLink] App received URL while running');
          handleAppUrlOpen(event);
        });

        // Handle the URL that launched the app (critical for cold starts)
        const launchUrl = await CapApp.getLaunchUrl();
        if (launchUrl && launchUrl.url) {
          console.log('[DeepLink] App launched with URL (cold start):', launchUrl.url);
          // Small delay to ensure the app is fully hydrated before processing auth
          setTimeout(() => handleAppUrlOpen(launchUrl), 500);
        }
      } catch (e) {
        console.warn('[DeepLink] Could not initialize App plugin', e);
      }
    };

    if ((window as any).Capacitor?.isNativePlatform()) {
      setupDeepLinkListener();
    }
  }, []);

  // [SEC-M5] Strip OAuth tokens from the URL after Supabase consumes them
  // on web. Tokens in window.location.hash leak via browser history, the
  // Referer header on subsequent navigations, and any third-party scripts
  // that read the URL. Supabase's detectSessionInUrl extracts the tokens
  // synchronously at client init, so by the time we run we can safely clear
  // the hash without losing the session.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const hasTokenInHash =
      window.location.hash.includes('access_token=') ||
      window.location.hash.includes('refresh_token=');
    const hasTokenInQuery =
      window.location.search.includes('access_token=') ||
      window.location.search.includes('refresh_token=');
    if (!hasTokenInHash && !hasTokenInQuery) return;
    try {
      window.history.replaceState({}, '', window.location.pathname);
    } catch (e) {
      console.warn('[Auth] Failed to strip tokens from URL', e);
    }
  }, []);

  return (
    <ThemeProvider
      attribute="class"
      defaultTheme="system"
      enableSystem
      disableTransitionOnChange
    >
      <SettingsProvider>
        <BackendStatusProvider>
        <EnvironmentThemeHandler />
        <QueryClientProvider client={queryClient}>
          <TooltipProvider>
            <AuthProvider>
              <AppUsageTracker />
              <AuthQueryCacheBuster />
              <TeamProvider>
                <ErrorContextManager />
                <TeamThemeHandler />
                <TagsProvider>
                  <FamilyProvider>
                    <DiscoveryProvider>
                      <Toaster />
                      <Sonner />
                      <ToastSettingsManager />
                      <AppUpdateManager />
                      <TaskEventHandler />
                      <StockEventHandler />
                      <MealEventHandler />
                      <FailedSyncAutoRetry />
                      <NetworkStatusBanner />
                      <BackgroundPrefetcher />

                      <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
                        <SkipToContent />
                        <div id={SKIP_TO_CONTENT_TARGET_ID} tabIndex={-1} className="outline-none">
                        <Suspense fallback={null}>
                          <Routes>
                            {/* Protected routes - require auth in production */}
                            <Route path="/" element={<AuthGate><Index /></AuthGate>} />
                            <Route path="/admin" element={<AuthGate><AdminPageComp /></AuthGate>} />
                            <Route path="/:teamSlug" element={<AuthGate><TeamPage /></AuthGate>} />

                            {/* Public routes - always accessible */}
                            <Route path="/install" element={<InstallPage />} />
                            <Route path="/vs" element={<CompareIndex />} />
                            <Route path="/vs/:competitor" element={<Compare />} />
                            <Route path="/app-redirect" element={<AppRedirect />} />
                            <Route path="/share" element={<AuthGate><ShareTarget /></AuthGate>} />
                            <Route path="/authorize-device" element={<AuthorizeDevicePage />} />
                            <Route path="/webhook/:slug" element={<WebhookHandler />} />
                            <Route path="/admin/users" element={<AuthGate><SuperAdminUsersView /></AuthGate>} />

                            {/* API/Action Routes - require auth */}
                            <Route path="/api/tasks/:taskId/:action" element={<AuthGate><TaskActionHandler /></AuthGate>} />
                            <Route path="/api/create-task" element={<AuthGate><TaskCreateHandler /></AuthGate>} />

                            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                            <Route path="*" element={<NotFound />} />
                          </Routes>
                        </Suspense>
                        </div>
                      </BrowserRouter>
                    </DiscoveryProvider>
                  </FamilyProvider>
                </TagsProvider>
              </TeamProvider>
            </AuthProvider>
          </TooltipProvider>
        </QueryClientProvider>
      </BackendStatusProvider>
      </SettingsProvider>
    </ThemeProvider>
  );
};

export default App;
